import { useState, type JSX } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Label, Select, SelectOption, useToast } from '@wistia/ui';
import type { TimeCodedTranscript } from '~/graphql';
import { trackStructEvent } from '~/utilities/snowplow';
import { DownloadButton, SelectWrapper, TabWrapper } from './SharedStyledComponents';

type Format = 'srt' | 'txt' | 'vtt';

type Props = {
  mediaHashedId: string;
  defaultIetfLanguageTag: string;
  transcripts: TimeCodedTranscript[];
  isAudio: boolean;
};

export const TranscriptTab = ({
  defaultIetfLanguageTag,
  mediaHashedId,
  transcripts,
  isAudio,
}: Props): JSX.Element => {
  const showToast = useToast();
  const [selectedLanguage, setSelectedLanguage] = useState(defaultIetfLanguageTag);
  const [selectedFormat, setSelectedFormat] = useState<Format>('srt');

  const changeLanguage = (language: string) => setSelectedLanguage(language);
  const changeFormat = (format: Format) => setSelectedFormat(format);

  const downloadTranscript = async () => {
    const downloadParams = new URLSearchParams();
    downloadParams.set('disposition', 'attachment');
    downloadParams.set('ietf_language_tag', selectedLanguage);
    const baseUrl = `/medias/${mediaHashedId}/time_coded_transcripts/download.${selectedFormat}`;
    const url = `${baseUrl}?${downloadParams}`;

    try {
      const response = await fetch(url);

      if (response.ok) {
        window.open(response.url, '_blank');

        trackStructEvent({
          category: 'button',
          action: 'click',
          label: `publish-download-transcript${isAudio ? '-audio' : ''}`,
          property: 'language',
          value: selectedLanguage,
        });
      } else {
        throw new Error(response.statusText || 'Error downloading transcript');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      showToast({
        message: 'Error downloading transcript',
        colorScheme: 'error',
      });
    }
  };

  const handleDownload = () => {
    void downloadTranscript();
  };

  return (
    <TabWrapper>
      <SelectWrapper>
        <Label>Language</Label>
        <Select
          onChange={changeLanguage}
          fullWidth={true}
          value={selectedLanguage}
          disabled={transcripts.length === 1}
        >
          {transcripts.map(({ englishName, ietfLanguageTag }) => (
            <SelectOption
              key={ietfLanguageTag}
              value={ietfLanguageTag}
            >
              {englishName}
            </SelectOption>
          ))}
        </Select>
      </SelectWrapper>

      <SelectWrapper>
        <Label>Format</Label>
        <Select
          onChange={changeFormat}
          fullWidth={true}
          value={selectedFormat}
        >
          <SelectOption value="srt">SRT (SubRip subtitle)</SelectOption>
          <SelectOption value="vtt">VTT (Video Text Tracks)</SelectOption>
          <SelectOption value="txt">TXT (Plain text)</SelectOption>
        </Select>
      </SelectWrapper>

      <DownloadButton
        onClick={handleDownload}
        data-testid="download-transcript"
      >
        Download
      </DownloadButton>
    </TabWrapper>
  );
};
